import { z } from "zod";

/**
 * Central source of truth for all feature flags.
 * Add new flags here. Keys are stable identifiers stored in DB JSON.
 */
export const FEATURE_FLAGS = {
  UTILIZATION_TOOL: "utilization-tool",
  UTILIZATION_SETTINGS: "utilization-settings",
  EMERGENCY_OUTCOME: "emergency-outcome",
  CAMPAIGN_OPT_IN_GATE: "campaign-opt-in-gate",
  EXPOSE_CAMPAIGN_OPT_IN_SOURCE: "expose-campaign-out-in-source",
  CXR_BOOKING: "cxr-booking",
  DOUBLE_REACH_OUT_MESSAGE: "double-reach-out-message",
  CAPACITY_SETTINGS_V2: "capacity-settings-v2",
  USE_HOLIDAY_SCHEDULE_OVERRIDES_NEW: "use-holiday-schedule-overrides-new",
  /** Include AI-generated call summaries in the customer notes list */
  INCLUDE_AI_NOTES_IN_CUSTOMER_NOTES: "include-ai-notes-in-customer-notes",
  /** Hide the Transfers nav item from the settings sidebar */
  HIDE_TRANSFERS_NAV: "hide-transfers-nav",
  /** Hide all emergency service UI from the dashboard (sidebar, hours settings, holiday settings) */
  HIDE_EMERGENCY_SERVICE: "hide-emergency-service",
  /** Hide the Analytics nav item from the sidebar */
  HIDE_ANALYTICS_NAV: "hide-analytics-nav",
  /** Hide the Campaigns nav item from the sidebar */
  HIDE_CAMPAIGNS_NAV: "hide-campaigns-nav",
  /** Enable the Call Scoring feature */
  ENABLE_CALL_SCORING: "enable-call-scoring",
  /** Enable the Interactions Reason Drilldown Sankey view */
  INTERACTIONS_REASON_DRILLDOWN_SANKEY: "interactions-reason-drilldown-sankey",
  /** Enable hub folder feature (sidebar folders, folder dropdown) */
  HUB_FOLDERS_ENABLED: "hub-folders-enabled",

  /** Auto-flag all inbound text messages for human review (bypasses AI) */
  AUTO_FLAG_ALL_INBOUND_TEXTS: "auto-flag-all-inbound-texts",
  /** Enable the Greenhouse / Opportunity Center feature */
  ENABLE_GREENHOUSE: "enable-greenhouse",
  /** Show the "Is Netic first" filter in analytics inbound dashboard */
  SHOW_IS_N1_FILTER: "show-is-n1-filter",
  /** Use chat-v2 endpoint for inbound comms instead of chat-async */
  USE_CHAT_V2_FOR_INBOUND_COMMS: "use-chat-v2-for-inbound-comms",
  /** Show the Classifiers V1 banner and allow swapping to old DrilldownSankey */
  SHOW_INBOUND_ANALYTICS_V1: "show-inbound-analytics-v1",

  /** Enable shadow mode for rules engine pricing (runs both old and new, returns old, logs comparison) */
  RULES_ENGINE_PRICING_SHADOW: "rules-engine-pricing-shadow",
  /** Enable active mode for rules engine pricing (uses new engine, falls back to old if no rules) */
  RULES_ENGINE_PRICING_ACTIVE: "rules-engine-pricing-active",
  /** Show the export button on the drilldown sankey analytics page */
  SHOW_DRILLDOWN_EXPORT: "show-drilldown-export",
  /** Route ASK_FOR_HUMAN transfers to NETIC_CSR department instead of default */
  NETIC_CSR_TRANSFER_ROUTING: "netic-csr-transfer-routing",
  /** Show the CSR transfer button in Hub for call_taker users */
  CSR_TRANSFER_BUTTON_ENABLED: "csr-transfer-button-enabled",
  /** Show recent transferred calls instead of live transferred_internal calls for Netic call-takers */
  NETIC_CALL_TAKER_RECENT_TRANSFERS: "netic-call-taker-recent-transfers",
  /** Enable PestPac payment confirmation flow (hosted payment URL + voice prompt) for ISP bookings */
  ENABLE_PAYMENT_CONFIRMATION: "enable-payment-confirmation",
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

export type FeatureFlagMap = Record<FeatureFlagKey, boolean>;

// create a zod schema that should chnage with the FEATURE_FLAG object
const featureFlagDbShape: Record<FeatureFlagKey, z.ZodOptional<z.ZodBoolean>> = (
  Object.values(FEATURE_FLAGS) as FeatureFlagKey[]
).reduce(
  (acc, key) => {
    acc[key] = z.boolean().optional();
    return acc;
  },
  {} as Record<FeatureFlagKey, z.ZodOptional<z.ZodBoolean>>
);

export const featureFlagsDbSchema = z.object(featureFlagDbShape).partial();

/**
 * Parse raw DB value (unknown JSON) into a complete FeatureFlagMap, defaulting missing flags to false.
 */
export function parseDbFeatureFlags(raw: unknown): FeatureFlagMap {
  const parsed = featureFlagsDbSchema.catch({}).parse(raw ?? {});
  const allKeys = Object.values(FEATURE_FLAGS) as FeatureFlagKey[];
  const materialized: FeatureFlagMap = Object.fromEntries(
    allKeys.map(k => [k, Boolean(parsed[k] ?? false)])
  ) as FeatureFlagMap;
  return materialized;
}

/**
 * Validate a single flag key string.
 */
export function isKnownFeatureFlag(flag: string): flag is FeatureFlagKey {
  return (Object.values(FEATURE_FLAGS) as string[]).includes(flag);
}
